"use client"

import { useEffect } from "react"
import { buildThemeCss, AVAILABLE_FONTS, fontFamilyCss, type ThemeSettings } from "@/lib/site-settings"

/**
 * Fetches the admin-customized theme (cached endpoint) and applies it to the
 * live site by:
 *  - injecting `:root` CSS variables,
 *  - hot-loading the configured Google Fonts,
 *  - toggling `.dark` on <html> when dark mode is enabled.
 *
 * The admin panel itself is scoped by `.admin` tokens and is not affected.
 */
export function ThemeStyle() {
  useEffect(() => {
    let cancelled = false

    async function apply() {
      try {
        const res = await fetch("/api/public/theme", { cache: "no-store" })
        if (!res.ok) return
        const theme = (await res.json()) as ThemeSettings
        if (cancelled) return

        // 1. CSS variables
        const styleEl = document.getElementById("vantage-theme-css") as HTMLStyleElement | null
        const nextStyle = styleEl ?? document.createElement("style")
        nextStyle.id = "vantage-theme-css"
        nextStyle.textContent = buildThemeCss(theme)
        if (!styleEl) document.head.appendChild(nextStyle)

        // 2. Google Fonts (only for selectable fonts that aren't preloaded)
        const fontsToLoad = [theme.fontFamily, theme.headingFont].filter(
          (f): f is string =>
            Boolean(f) &&
            (AVAILABLE_FONTS as readonly string[]).includes(f) &&
            f !== "Inter" &&
            f !== "Playfair Display"
        )
        for (const font of fontsToLoad) {
          const key = font.toLowerCase().replace(/\s+/g, "-")
          if (!document.getElementById(`font-${key}`)) {
            const link = document.createElement("link")
            link.id = `font-${key}`
            link.rel = "stylesheet"
            link.href = `https://fonts.googleapis.com/css2?${fontFamilyCss(font)}`
            document.head.appendChild(link)
          }
        }

        // 3. Dark mode
        document.documentElement.classList.toggle("dark", theme.darkMode === true)
      } catch (error) {
        console.error("Failed to apply theme:", error)
      }
    }

    apply()

    return () => {
      cancelled = true
    }
  }, [])

  // This component performs its work in the effect above and renders nothing —
  // the CSS is injected straight into <head> to avoid FOUC.
  return null
}